/**
 * Small shared FAR UI pieces: state tones, icons, status pill, notice box.
 * Every state is shown as icon + text + color (never color alone).
 */
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import ReportProblemOutlined from "@material-ui/icons/ReportProblemOutlined";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import { farTokens } from "../../../../assets/jss/user/far/farTokens.jsx";
import { FAR_STATE } from "../../../../assets/js/utils/far/farEngine";

export const FAR_STATE_TONE = {
    [FAR_STATE.NONE]: "neutral",
    [FAR_STATE.MATCHED]: "info",
    [FAR_STATE.AUTHORIZED]: "success",
    [FAR_STATE.OVERRIDDEN]: "warning",
    [FAR_STATE.MIXED]: "warning",
    [FAR_STATE.UNAUTHORIZED]: "danger",
    [FAR_STATE.LINK]: "danger",
    [FAR_STATE.EXCLUDED]: "danger"
};

export const toneIcon = (tone) => {
    switch(tone){
        case "success": return CheckCircle;
        case "danger": return ErrorOutline;
        case "warning": return ReportProblemOutlined;
        case "shield": return VerifiedUser;
        default: return InfoOutlined;
    }
};

const useStyles = makeStyles(theme => {
    const t = farTokens(theme);
    const tone = (key) => ({
        color: t[key].fg,
        backgroundColor: t[key].bg,
        borderColor: t[key].border,
        "& $icon": { color: t[key].solid === "#64748B" ? t[key].fg : (t.dark ? t[key].fg : t[key].solid) }
    });
    return {
        pill: {
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "3px 10px 3px 8px",
            borderRadius: "999px",
            border: "1px solid transparent",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "18px",
            whiteSpace: "nowrap"
        },
        notice: {
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "10px 12px",
            borderRadius: "8px",
            border: "1px solid transparent",
            fontSize: "14px",
            lineHeight: "20px",
            textAlign: "left",
            "& p": { margin: 0 },
            "& strong": { fontWeight: 700 }
        },
        noticeBody: { flex: 1, minWidth: 0, wordBreak: "break-word" },
        icon: { width: "18px !important", height: "18px !important", flexShrink: 0, marginTop: "1px" },
        success: tone("success"),
        danger: tone("danger"),
        warning: tone("warning"),
        info: tone("info"),
        neutral: tone("neutral")
    };
});

export function FarPill({ tone = "neutral", children, icon = true, className = "" }){
    const classes = useStyles();
    const Icon = toneIcon(tone);
    return (
        <span className={classes.pill+" "+classes[tone]+" "+className}>
            {icon ? <Icon className={classes.icon} aria-hidden="true" /> : null}
            <span>{children}</span>
        </span>
    );
}

export function FarNotice({ tone = "info", children, action = null, className = "", role = null, id = null }){
    const classes = useStyles();
    const Icon = toneIcon(tone);
    const ariaRole = role || (tone === "danger" || tone === "warning" ? "alert" : "status");
    return (
        <div className={classes.notice+" "+classes[tone]+" "+className} role={ariaRole} id={id || undefined}>
            <Icon className={classes.icon} aria-hidden="true" />
            <div className={classes.noticeBody}>{children}</div>
            {action}
        </div>
    );
}

export const ruleStatusLabel = (active) => (active ? "Active" : "Inactive");

export const formatFarDate = (value, withTime = false) => {
    if(!value){
        return "";
    }
    const date = new Date(value);
    if(isNaN(date.getTime())){
        return String(value);
    }
    const options = { month: "short", day: "numeric", year: "numeric" };
    if(withTime){
        options.hour = "numeric";
        options.minute = "2-digit";
    }
    return date.toLocaleString(undefined, options);
};
